import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';


const tabs = [
  { label: 'For Everyone' },
  { label: 'Background' },
  { label: 'Clients' },
  { label: 'Collaborators' }
];

const tabContents: { [key: string]: { title: string; description: string } } = {
  'For Everyone': {
    title: "Hi, welcome to my portfolio.",
    description: "Hi, I'm a passionate UI/UX Designer with a strong foundation in frontend development, driven by a desire to create digital experiences that are not only beautiful but also intuitive and user-centered. With a background in both design and development, I thrive at the intersection of creativity and functionality. Over the past few years, I've had the opportunity to design and build web experiences from the ground up turning ideas into responsive, interactive, and user-friendly interfaces. I specialize in using tools such as Figma, Adobe Photoshop, React, and JavaScript to transform concepts into functional, user-centered experiences always ensuring a balance between aesthetics and usability. Whether I'm wireframing user flows or developing interactive components, my focus remains on crafting solutions that address real-world problems and deliver meaningful value to users. "
  },
  'Background': {
    title: "My Background",
    description: "I'm Manish Shrestha, a UI/UX Designer with a strong foundation in frontend development. I create intuitive, user-focused digital experiences that balance visual appeal with technical functionality. With experience at Vaidya Energy, Velox Labs, and Solveetech, I've designed and developed responsive web and mobile platforms using tools like Figma, React.js, and JavaScript. I hold a Master's in Advanced Computer Science from The British College and a Bachelor's in Computer Science from the University of Wolverhampton. "
  },
  'Clients': {
    title: "For Clients",
    description: "I leverage my expertise in design, user experience, and creative leadership to help brands stand out and connect with their audiences in meaningful ways. By blending thoughtful design strategy with hands-on execution, I craft digital experiences that are not only visually engaging but also intuitive, purposeful, and aligned with business goals. My approach is rooted in collaboration, innovation, and a deep understanding of user needs — ensuring that every project delivers lasting value. Let's work together to turn your vision into a powerful, impactful digital reality."
  },
  'Collaborators': {
    title: "For Collaborators",
    description: "Let's collaborate to push the boundaries of what's possible at the intersection of design and technology. I'm passionate about creating seamless, meaningful digital experiences that not only look great but also solve real problems. Whether it's refining user flows, building interactive interfaces, or transforming complex ideas into simple solutions, I believe in designing with purpose and developing with precision. Together, we can craft innovative products that inspire users and drive lasting impact."
  }
};

const HeroIntroSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState('For Everyone');
  const content = tabContents[activeTab];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <section className="relative min-h-[60vh] flex flex-col bg-white bg-gradient-to-b from-[#f8f8f8] to-white px-4 sm:px-6 lg:px-8 font-sans py-16 overflow-hidden md:rounded-t-3xl" data-bg="light">
    
      <motion.div 
        className="relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto w-full flex flex-col items-start text-left pt-8 mb-12">
          {/* Tabs with enhanced animations */}
          <motion.div 
            className="flex flex-wrap gap-2 sm:gap-x-2 mb-10"
            variants={itemVariants}
          >
            {tabs.map((tab, index) => (
              <motion.button
                key={tab.label}
                onClick={() => setActiveTab(tab.label)}
                className={`px-5 py-3 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 transform hover:scale-105
                  ${activeTab === tab.label
                    ? 'bg-black text-white shadow-lg'
                    : 'text-black hover:bg-gray-200 hover:shadow-md'
                  }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
              >
                {tab.label}
              </motion.button>
            ))}
          </motion.div>

          {/* Main Intro Text with enhanced animations */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-full"
            >
              <motion.h1 
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-black leading-tight mb-6 font-sans tracking-tight"
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                {content.title}
              </motion.h1>
              <motion.p 
                className="text-base sm:text-lg md:text-xl text-gray-700 max-w-7xl"
                variants={textVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.2 }}
              >
                {content.description}
              </motion.p>
            </motion.div>
          </AnimatePresence>
        </div>
      
        <motion.div 
          className="max-w-7xl mx-auto w-full flex flex-col md:flex-row items-center md:items-end justify-between space-y-6 md:space-y-0 font-sans"
          variants={itemVariants}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="text-gray-500 mb-1 text-sm sm:text-base">Location</div>
            <div className="space-x-2 text-base sm:text-lg">
              <span className="font-bold text-black">Bhaktapur, Nepal</span>
            </div>
          </motion.div>
          <motion.a
            href="/work"
            className="flex items-center space-x-2 text-gray-700 hover:text-white text-base sm:text-lg font-normal border border-gray-400 rounded-full px-6 sm:px-8 py-2 sm:py-3 transition-all duration-300 md:ml-auto font-sans bg-white hover:bg-black transform hover:scale-105 shadow hover:shadow-lg"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <span>View my work</span>
            <motion.span 
              className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center border border-gray-400 rounded-full ml-2"
              whileHover={{ rotate: 90 }}
              transition={{ duration: 0.3 }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2s" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
            </motion.span>
          </motion.a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroIntroSection; 